// =======================================
// DATA TYPES
// =======================================

/**
 * Unified workflow item representing either a project or stage
 * This follows the same pattern as job_cards and timesheet_project_options
 */
export interface WorkflowItem {
  id: number;
  job_number: string;  // "15955" for project, "15955.1" for stage
  description: string;
  parent_description: string | null;  // Parent project description for stages
  types: string;       // Inherited from parent for stages
  workflow_status_id: number | null;
  workflow_status: string | null;
  display_order: number | null;
  entity_type: 'project' | 'stage';
  parent_project_id: number | null;  // null for projects
  stage_id: string | null;           // null for projects
  project_lead: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  client: {
    id: number;
    company_name: string;
  } | null;
  // RWC-specific fields
  startup_meeting: boolean | null;
  requires_qa: boolean | null;
  number_of_inspections: number | null;
  start_up_completion_date: string | null;
  qa_completion_date: string | null;
  inspection_count: number;
  /** Date (no time) of the most recent inspection; null when there are none. */
  last_inspection_date: string | null;
  qa_required_count: number;
  qa_completed_count: number;
  region: string | null;
  created_at: string | null;
  /** When the item last changed column; null if it hasn't since this was recorded. */
  workflow_status_changed_at: string | null;
  // Tags
  tags: { id: number; name: string }[];
  // Last activity message
  last_message: {
    message: string;
    sender_first_name: string;
    created_at: string;
  } | null;
}

/**
 * How a card names its job: the bold title and, for a stage, the line under
 * it. A stage is titled by its project, as the job is known, with the stage's
 * own name under it; the RWD board shows only stages, so titling a card by the
 * stage hides every project name on that board. The Move to sheet uses the
 * same names.
 */
export const workflowItemNames = (item: WorkflowItem): { title: string; stageLine: string | null } =>
  item.entity_type === 'stage' && item.parent_description
    ? { title: item.parent_description, stageLine: item.description }
    : { title: item.description, stageLine: null };

/**
 * Workflow status definition with ordering per type
 */
export interface WorkflowStatus {
  id: number;
  status_name: string;
  display_order: number;
}

/**
 * Workflow type definitions
 */
export type WorkflowType = 'Design' | 'Advisory' | 'RWC' | 'RWD';

/**
 * Filter configuration for workflow items
 */
export interface WorkflowFilters {
  search_term?: string | null;
  project_lead_filter?: string[] | null;
  client_filter?: string[] | null;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Available workflow types with their display labels and route paths
 */
export const WORKFLOW_TYPES: { type: WorkflowType; label: string; path: string }[] = [
  { type: 'Design', label: 'Design', path: 'design' },
  { type: 'Advisory', label: 'Advisory', path: 'advisory' },
  { type: 'RWC', label: 'RWC', path: 'rwc' },
  { type: 'RWD', label: 'RWD', path: 'rwd' }
];

/**
 * Map route path to workflow type
 */
export const PATH_TO_TYPE: Record<string, WorkflowType> = {
  design: 'Design',
  advisory: 'Advisory',
  rwc: 'RWC',
  rwd: 'RWD'
} as const;

/**
 * Default search debounce delay in milliseconds
 */
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Region options for RWC projects filter
 */
export const RWC_REGION_OPTIONS = [
  'South',
  'North',
  'North East',
  'South East'
] as const;

// =======================================
// BOARD HELPERS
// =======================================

/**
 * The order cards take inside a column: newest project first, and a
 * project's stages in stage order under it. The board has no manual order --
 * dragging only changes a card's column -- so this is the only order, and it
 * is the same on every load.
 */
export const compareWorkflowItems = (a: WorkflowItem, b: WorkflowItem): number => {
  const [aJob, aStage] = a.job_number.split('.');
  const [bJob, bStage] = b.job_number.split('.');
  const aNum = Number(aJob);
  const bNum = Number(bJob);
  if (aJob !== bJob) {
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return bNum - aNum;
    return bJob.localeCompare(aJob, undefined, { numeric: true });
  }
  // Same project: the project itself, then its stages ascending.
  if (!aStage) return bStage ? -1 : 0;
  if (!bStage) return 1;
  return aStage.localeCompare(bStage, undefined, { numeric: true });
};

/**
 * The column an item sits in: its own status when that is a column on this
 * board, otherwise the first column (an item with no status yet). The one
 * rule for both the lanes and the rail's counts.
 */
export const columnIdOf = (item: WorkflowItem, statuses: WorkflowStatus[]): number | null => {
  if (item.workflow_status_id && statuses.some(s => s.id === item.workflow_status_id)) {
    return item.workflow_status_id;
  }
  return statuses[0]?.id ?? null;
};

/** Items grouped into their columns, each column in the board's fixed order. */
export const groupItemsByStatus = (
  items: WorkflowItem[],
  statuses: WorkflowStatus[],
): Map<number, WorkflowItem[]> => {
  const grouped = new Map<number, WorkflowItem[]>(statuses.map(s => [s.id, []]));
  const firstId = statuses[0]?.id;
  items.forEach(item => {
    const id = item.workflow_status_id && grouped.has(item.workflow_status_id)
      ? item.workflow_status_id
      : firstId;
    if (id !== undefined) grouped.get(id)?.push(item);
  });
  grouped.forEach(list => list.sort(compareWorkflowItems));
  return grouped;
};

/**
 * Column rail labels. The rail holds every column of a board on one line, so
 * the long status names are shortened there; the column header keeps the
 * full name.
 */
export const SHORT_STATUS_LABELS: Record<string, string> = {
  'Schematic Design': 'Schematic',
  'Design Development': 'Development',
  'Contract Documentation': 'Documentation',
  'Defect Liability': 'Defects',
  'Inspections Invoice': 'Insp. invoice',
  'Final Invoice': 'Final invoice',
  'QA Review': 'QA review',
};

export const shortStatusLabel = (statusName: string): string =>
  SHORT_STATUS_LABELS[statusName] ?? statusName;

/**
 * Columns collapsed to a slim rail until the viewer opens them. The finished
 * states: nobody works from them, but they stay drop targets.
 */
export const DEFAULT_COLLAPSED_STATUSES = ['Complete', 'Archived'];
